import { Link, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import AppFooter from '../../components/AppFooter';
import { Button } from '../../components/ui/button';
import { useDocumentMeta } from '../../lib/useDocumentMeta';
import { GUIDES, GUIDE_CATEGORIES } from '../../lib/guides';

const CATEGORY_ORDER = [GUIDE_CATEGORIES.START, GUIDE_CATEGORIES.ORG, GUIDE_CATEGORIES.UNDERSTAND];

export const GuidesIndex = () => {
  const navigate = useNavigate();

  useDocumentMeta({
    title: 'Guides to requesting your care, school, health and police records | Precious Data',
    description:
      'Plain-English guides to requesting your own records as a care leaver: fostering, adoption, school, medical and police files, what the law entitles you to, and what to do if an organisation refuses.',
    path: '/guides',
  });

  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="guides-index-page">
      <PublicHeader />

      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
            Free guides
          </span>
          <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98]">
            How to request your own records
          </h1>
          <p className="text-[19px] leading-[1.5] text-ink-70">
            Practical, step-by-step guides to Subject Access Requests for people who grew up in
            care or were adopted — who to ask, what to say, how long it should take, and what
            your rights are if an organisation drags its feet. Every guide links to the actual
            law and the Information Commissioner&apos;s Office, not just our summary of it.
          </p>
          <Button size="lg" onClick={() => navigate('/register')} data-testid="guides-hero-register-btn">
            Or let us do it for you — create a free account
          </Button>
        </div>
      </section>

      {CATEGORY_ORDER.map((category) => (
        <section key={category} className="px-7 lg:px-12 py-10">
          <div className="max-w-6xl mx-auto space-y-6">
            <h2 className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">{category}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {GUIDES.filter((g) => g.category === category).map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/guides/${guide.slug}`}
                  className="bg-ground rounded-lg p-6 space-y-2 hover:opacity-90 transition-opacity duration-state"
                  data-testid={`guide-card-${guide.slug}`}
                >
                  <p className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">{guide.title}</p>
                  <p className="text-[15px] text-ink-70 leading-relaxed">{guide.dek}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-3xl mx-auto space-y-5">
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
            Or skip the reading and let us write the letters.
          </h2>
          <p className="text-ink-70 leading-relaxed">
            Precious Data is free for care leavers. Tell us what you remember and we&apos;ll draft
            the requests, send them, track every legal deadline, and chase anyone who goes quiet
            — then bring what comes back together so you can actually make sense of it.
          </p>
          <div className="pt-2">
            <Button size="lg" onClick={() => navigate('/register')} data-testid="guides-footer-register-btn">
              Create a free account
            </Button>
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

export default GuidesIndex;
